import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconSms } from '@components/icons/phone-sms';
import { IconSend } from '@components/icons/send';
import { LabelTextGroup } from '@components/label-text-group';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingVariables } from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';
import { formatPhoneNumber } from '@utils/strings';

import { useActionSmsTemplate } from 'api/journeys/hooks';
import { useUpdateJourneyTravellerAction } from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { getActionStatus } from 'ui/authenticated/main/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { PreviewSmsMenuItemView } from './preview-sms-menu-item';
import { usePreviewSmsModal } from './preview-sms-modal';
import { buildSmsPreviewPropsFromTravellerAction } from './sms-preview';

import {
  ActionProgressBar,
  ActionResultCard,
  FormSidebarFooter,
  SMSMessageCountWarning,
  SidebarProps,
  TravellerActionFooter,
  TravellerActionSidebar,
  TriggerMethod,
  isActionError,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';
import { brokenVariableLabels, replaceVariableTokensWithLabels } from '../shared/preview-variables';
import { renderWithVariableChips } from '../shared/variable-chips';

const ACTION_TYPE = ACTION_TYPES.send_sms;

export const SidebarContent = ({ shortId }: SidebarProps) => (
  <TravellerActionSidebar
    shortId={shortId}
    actionType={ACTION_TYPE}
    Details={DetailsContent}
    Edit={FormContent}
    renderMenuItems={(action) => (
      <PreviewSmsMenuItemView previewProps={buildSmsPreviewPropsFromTravellerAction(action)} />
    )}
  />
);

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const showSmsPreview = usePreviewSmsModal();

  return (
    <Stack space={6}>
      <ActionProgressBar action={action} isError={isError} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <SmsDetailCard action={action} hideStatus />
        </Stack>
      </HideContainer>
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('sendSMS')}
        retryText={t('resendSMS')}
        onPreview={() => showSmsPreview(buildSmsPreviewPropsFromTravellerAction(action))}
      />
    </Stack>
  );
};

type SMSData = {
  description: string;
  isAutomatic: boolean;
  recipients: EditorValue<{ variables?: FieldMappingVariables }>[];
  message: EditorValue<{ variables?: FieldMappingVariables }>;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
};

const Form = createForm<SMSData>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues() as SMSData;
  const isDirty = formStore.useIsDirty();
  const fieldMappingId = getFieldMappingId(action.journeyId);
  const baseVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const phoneVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables ?? [],
    journeyId: action.journeyId,
    types: ['phone'],
  });
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = (data: SMSData) => {
    return editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        recipients: data.recipients ?? [],
        message: data.message?.value ? data.message : {},
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            hideLabel
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('smsContent')}>
          <Stack space={4}>
            <Form.FieldMappingPhoneListField
              id="recipients"
              validators={['required']}
              variableGroups={phoneVariableGroups}
              label={t('recipients')}
              defaultValue={action.values?.recipients ?? []}
              fieldMappingId={fieldMappingId}
            />
            <Form.FieldMappingTextEditor
              id="message"
              label={t('message')}
              plaintext
              variableGroups={baseVariableGroups}
              defaultValue={action.values?.message}
              validators={['required']}
              fieldMappingId={fieldMappingId}
            />
            <SMSMessageCountWarning message={formValues?.message} />
          </Stack>
        </HideContainer>
      </Stack>
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

type SmsDetailCardProps = ContentProps & {
  hideStatus?: boolean;
};

export const SmsDetailCard = ({ action, hideStatus }: SmsDetailCardProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const showPreviewSmsModal = usePreviewSmsModal();

  const recipients = ((action.data?.recipients ?? []) as string[])
    .flatMap((receipient) => receipient.split(','))
    .map(formatPhoneNumber);

  const isError = isActionError(action);
  const status = getActionStatus(action);

  // SMS has no delivery-events pipeline (unlike email's Mailgun webhooks), so
  // a completed send only proves the carrier accepted it — "Sent" is the
  // honest claim, matching the email card's wording.
  const progressText =
    status === 'completed'
      ? t('sent')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notSent');

  const smsMessage = buildSmsPreviewPropsFromTravellerAction(action);

  return (
    <ActionResultCard
      action={action}
      title={t('smsMessage')}
      icon={<IconSms />}
      progressText={progressText}
      errorTitle={actionErrorTitle[action.action]}
      onPreview={() => showPreviewSmsModal(smsMessage)}
      hideStatus={hideStatus}
    >
      <LabelTextGroup label={t('sendTo')} text={recipients} />
      <Stack space={1}>
        <Text font="semibold14" text={t('message')} />
        <SmsMessageBody action={action} />
      </Stack>
    </ActionResultCard>
  );
};

const SmsMessageBody = ({ action }: ContentProps) => {
  // Backend hybrid leaves unresolved variables as {{ label }} tokens, shown as
  // pills; fall back to the relabelled template while the request is in flight.
  const { data: hybridMessage } = useActionSmsTemplate('traveller', action.shortId);
  const fallback = replaceVariableTokensWithLabels(
    action.values?.message?.value ?? '',
    action.values?.message?.variables
  );
  const brokenLabels = brokenVariableLabels(action.values?.message?.variables);
  const brokenLabelSet = brokenLabels.length ? new Set(brokenLabels) : undefined;
  return (
    <Text
      font="regular14"
      text={renderWithVariableChips(hybridMessage || fallback, brokenLabelSet)}
    />
  );
};

const sidebarLocale = {
  en: {
    sendSMS: 'Send SMS',
    dataNotFound: 'Data not found',
    description: 'Description',
    sendTo: 'Send to',
    message: 'Message',
    inProgress: 'In progress',
    notSent: 'Not sent',
    error: 'Error',
    resendSMS: 'Resend SMS',
    recipients: 'Send to',
    smsMessage: 'SMS message',
    sent: 'Sent',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    smsContent: 'SMS content',
  },
};
